import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { MessagingAccessToken, MessagingChannelBasic } from 'src/app/swagger-api';
import { ChatEngineLifeСycleService } from '../interfaces/chat-engine/chat-engine-lifecycle-service';
import { ChatEngineNotificationChannelService } from '../interfaces/chat-engine/chat-engine-notification-channel.service';
import { ChatEngineHandle } from '../interfaces/chat-model.interface';
import { ChatChannelService } from './cache-helpers/chat-channel.service';
import { ChatTokenService } from './cache-helpers/chat-token.service';

@Injectable({ providedIn: 'root' })
export class ChatAccessControlService implements OnDestroy {
  private subscriptions$: Subscription[] = [];

  private engineLifecycleService = inject(ChatEngineLifeСycleService);
  private engineNotificationChannelService = inject(ChatEngineNotificationChannelService);
  private chatChannelService = inject(ChatChannelService);
  private chatTokenService = inject(ChatTokenService);
  private sessionService = inject(SessionService);
  private chatInitialized = false;

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public subscribeToSession(): void {
    this.subscriptions$.push(
      this.sessionService.sessionChanges.subscribe((session) => {
        // Init chat engine after 2FA session
        if (session.user && this.sessionService.is2AuthLvlPassed && !this.chatInitialized) {
          this.chatInitialized = true;
          this.initChatEngine();
          return;
        }

        // Shutdown chat engine after logout
        if (!session.user && this.chatInitialized) {
          this.chatInitialized = false;
          this.shutdownChatEngine();
          return;
        }
      }),
    );
  }

  public initChatEngine(): void {
    this.chatTokenService.getData().subscribe((token: MessagingAccessToken) => {
      this.subscribeToAccessTokenLifecycle();
      this.subscribeToEngineReadinessChanged();

      this.engineLifecycleService.initEngine(token.access_token);
    });
  }

  public shutdownChatEngine(): void {
    this.engineLifecycleService.destroyEngine();

    unsubscribeAll(this.subscriptions$);
    this.subscribeToSession();
  }

  private subscribeToAccessTokenLifecycle(): void {
    this.subscriptions$.push(
      this.engineLifecycleService.expiredToken.subscribe(() => {
        this.engineLifecycleService.destroyEngine();

        this.chatTokenService.getData().subscribe((token: MessagingAccessToken) => {
          this.engineLifecycleService.initEngine(token.access_token);
        });
      }),
    );
  }

  private subscribeToEngineReadinessChanged(): void {
    this.subscriptions$.push(
      this.engineLifecycleService.engineReadinessChanged.subscribe((engineHandle: ChatEngineHandle) => {
        if (engineHandle.isValid()) {
          this.initNotificationChannel(engineHandle);
        } else {
          this.engineNotificationChannelService.closeNotificationChannel();
        }
      }),
    );
  }

  private initNotificationChannel(engineHandle: ChatEngineHandle): void {
    this.chatChannelService.getData().subscribe((response: MessagingChannelBasic) => {
      this.engineNotificationChannelService.openNotificationChannel(engineHandle, response.channel_external_id);
    });
  }
}
